import React, { FC } from 'react';
import { Form, useFormikContext } from 'formik';
import { Box, Button } from '@mui/material';
import { FieldText } from '../../fields/Text/Text';
import { FieldTextArea } from '../../fields/TextArea/TextArea';
import { FieldProductCategorySelect } from '../../fields/FieldProductCategoryAutocomplete/FieldProductCategorySelect';
import { ProductImage } from '../../../pages/admin/productsV2/productModal/ProductImage';
import { ImageProps, IProduct, ISchemaValues } from './constants';
import { BrandSelector } from './BrandSelector';
import { useUser } from '../../../hooks/useUser';
import { isInternal } from '../../../utils';
import { RmnSelector } from './RmnSelector';

interface Props {
  product?: IProduct;
  setFieldValue: (field: string, value: any) => void;
  image?: ImageProps;
  setImage: (image: ImageProps) => void;
  hideBrandFields?: boolean;
}

export const ProductFormElems: FC<Props> = ({
  product,
  setFieldValue,
  image,
  setImage,
  hideBrandFields,
}) => {
  const { role, brand } = useUser();
  const { values } = useFormikContext<ISchemaValues>();

  return (
    <Form className="flex flex-1 flex-col min-h-0">
      <Box className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto py-2 pb-4">
        <FieldText
          name="productName"
          label="Product Name"
          size="medium"
          fullWidth
          required
        />
        {/* <FieldText name="url" label="URL" size="medium" fullWidth /> */}

        {!hideBrandFields && Boolean(isInternal(role) || Boolean(brand?.some((b: any) => b?.rmn))) && (
          <>
            <BrandSelector setFieldValue={setFieldValue} />

            <RmnSelector />
          </>
        )}

        <FieldTextArea
          rows={5}
          name="productDescription"
          label="Product Description"
          required
          fullWidth
        />
        <Box className="flex flex-col gap-1">
          <FieldTextArea rows={3} name="disclaimers" label="Disclaimers" required={false} fullWidth />
          <span className="text-[12px] text-muted">
            Legal or claims language that must appear in this product&apos;s videos. Every new brief starts with it.
          </span>
        </Box>
        <FieldProductCategorySelect
          multiple
          name="product_category_ids"
          value={values.product_category_ids}
          setFieldValue={setFieldValue}
        />
        <ProductImage
          image={image}
          setImage={setImage}
          height={250}
          width={250}
        />
        {/* Returns estimate: Autopilot's reduce-returns objective reads
            returns / units until a returns feed exists. */}
        <Box className="flex flex-col gap-1">
          <span className="text-[13px] font-semibold text-text-1">Returns (estimate)</span>
          <span className="text-[12px] text-muted">
            Rough yearly figures are enough — the ratio shows whether product-page changes reduce returns.
          </span>
        </Box>
        <Box className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldText
            name="estimated_annual_units"
            label="Estimated annual units sold"
            size="medium"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />
          <FieldText
            name="estimated_annual_returns"
            label="Estimated annual returns"
            size="medium"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />
        </Box>
      </Box>
      <Box
        marginTop={2}
        width="100%"
        display="flex"
        justifyContent="flex-end"
        className="shrink-0 border-t border-border-token bg-surface pt-3 pb-1"
      >
        <Button
          size="large"
          type="submit"
          variant="contained"
          disableElevation
          className="!capitalize"
        >
          {product ? 'Save Changes' : 'Create Product'}
        </Button>
      </Box>
    </Form>
  );
};
